import { BAOCustomElement } from '@bao/custom-element'
import { Cart } from '@bao/cart'
import { sectionRenderer } from '@bao/section-rendering'

class GiftMessage extends BAOCustomElement() {
  constructor () {
    super()

    this._loading = false
  }

  connectedCallback () {
    super.connectedCallback()

    this.loading = false
    this.message = ''
  }

  setupListeners () {
    super.setupListeners()

    if (!this.els.form?.exists) return

    this.listeners.add(
      this.els.toggle.element,
      'toggle',
      this.handleToggle.bind(this),
    )

    this.listeners.add(
      this.els.message.element,
      'input',
      this.handleInput.bind(this),
    )

    this.listeners.add(
      this.els.form.element,
      'submit',
      this.handleSubmit.bind(this),
    )

    if (this.els.remove?.exists) {
      this.listeners.add(
        this.els.remove.element,
        'click',
        this.removeGiftMessage.bind(this),
      )
    }
  }

  handleToggle () {
    if (this.els.toggle.element.hasAttribute('open')) {
      this.enableFields()
    } else {
      this.disableFields()
    }
  }

  enableFields () {
    this.els.message.element.removeAttribute('disabled')
    this.els.submit.element.removeAttribute('disabled')

    setTimeout(() => {
      this.els.message.element.focus()

      const prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      ).matches

      this.els.message.element.scrollIntoView({
        behavior: prefersReducedMotion ? 'instant' : 'smooth',
        block: 'center',
        inline: 'center',
      })
    }, 0)
  }

  disableFields () {
    this.els.submit.element.setAttribute('disabled', '')
    this.els.message.element.setAttribute('disabled', '')
  }

  handleInput () {
    const regex = new RegExp(this.regex, 'gi')
    const value = this.els.message.element.value

    const hasError = !regex.test(value) && value

    if (hasError) {
      this.els.message.element.setCustomValidity(this.invalidText)
      this.els.message.element.setAttribute('aria-invalid', true)
      this.els.error.element.removeAttribute('hidden')
    } else {
      this.els.message.element.setCustomValidity('')
      this.els.message.element.setAttribute('aria-invalid', false)
      this.els.error.element.setAttribute('hidden', '')
    }
  }

  handleSubmit (event) {
    event.preventDefault()

    this.message = this.els.message.element.value?.trim() || ''

    if (this.message) {
      return this.updateGiftMessage()
    }

    return this.removeGiftMessage()
  }

  async updateGiftMessage () {
    this.loading = true

    try {
      const properties = {
        ...this.properties,
      }

      properties['Gift message'] = this.message ? this.message : null

      return await Cart.updateItemProperties(
        this.itemKey,
        this.quantity,
        properties,
        {
          sections: this.mappedSections,
        },
      )
    } catch (error) {
      console.error('Failed to update gift message', error)

      return null
    } finally {
      this.loading = false
    }
  }

  removeGiftMessage () {
    if (this.savedMessage) {
      this.message = ''

      return this.updateGiftMessage()
    }

    if (this.els.message?.element) {
      this.els.message.element.value = ''
    }

    this.els.toggle?.element?.removeAttribute('open')

    return null
  }

  get loading () {
    return this._loading
  }

  set loading (value) {
    this._loading = value
    this.setAttribute('aria-busy', value)
  }

  get itemKey () {
    return this.dataset.key
  }

  get savedMessage () {
    return this.dataset.savedMessage || ''
  }

  get properties () {
    try {
      const parsedProperties = JSON.parse(this.dataset.properties || '[]')

      if (Array.isArray(parsedProperties)) {
        return parsedProperties.reduce((properties, property) => {
          if (!Array.isArray(property) || property.length < 2) {
            return properties
          }

          const [key, value] = property

          if (typeof key !== 'string') {
            return properties
          }

          properties[key] = value

          return properties
        }, {})
      }

      if (parsedProperties && typeof parsedProperties === 'object') {
        return {
          ...parsedProperties,
        }
      }

      return {}
    } catch (error) {
      console.error('Failed to parse gift message properties', error)

      return {}
    }
  }

  get quantity () {
    return Number.parseInt(this.dataset.quantity, 10) || 1
  }

  get pushCart () {
    return this.closest('push-cart')
  }

  get inPushCart () {
    return this.pushCart != null
  }

  get cart () {
    return this.closest('cart-page')
  }

  get onCartPage () {
    return this.cart != null
  }

  get mappedSections () {
    let sections = this.inPushCart
      ? sectionRenderer.pushCartSections
      : this.onCartPage
        ? sectionRenderer.cartSections
        : []

    sections = sections.slice(0, 1)

    return (sections || []).map((section) => section.section).filter(Boolean)
  }

  get invalidText () {
    return this.dataset.invalidText || 'Invalid characters'
  }

  get regex () {
    return this.dataset.regex
  }
}

if (!customElements.get('gift-message')) {
  customElements.define('gift-message', GiftMessage)
}
